import { KeyRound, Send, UserCog, UserX } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { RowAction } from '@nucleus-commerce/admin-kit/index-table';
import type { Member } from '@nucleus-commerce/api-client/admin';

import { memberRestrictions, type Actor } from '../schemas/member-rules';

export type MemberRowAction = 'edit' | 'permissions' | 'resend' | 'remove';

export interface MemberRowActionsOptions {
  /** Signed-in member with `staff.manage`, or null when the viewer may only read (no row actions). */
  actor: Actor | null;
  members: readonly Member[];
  onAction: (action: MemberRowAction, member: Member) => void;
}

/** Per-row menu. Entries the backend would refuse (yourself, the last owner, an owner as non-owner) are hidden. */
export function useMemberRowActions({ actor, members, onAction }: MemberRowActionsOptions) {
  const { t } = useTranslation('team');

  return useMemo<Array<RowAction<Member>> | undefined>(() => {
    if (!actor) return undefined;
    const restrictions = (member: Member) => memberRestrictions(actor, member, members);
    return [
      {
        id: 'edit',
        label: t('edit.action'),
        icon: <UserCog aria-hidden />,
        hidden: (member) => !restrictions(member).canManage,
        onAction: (member) => onAction('edit', member),
      },
      {
        id: 'permissions',
        label: t('effective.action'),
        icon: <KeyRound aria-hidden />,
        hidden: (member) => !restrictions(member).canManage,
        onAction: (member) => onAction('permissions', member),
      },
      {
        id: 'resend',
        label: t('resend.action'),
        icon: <Send aria-hidden />,
        hidden: (member) => !restrictions(member).canResend,
        onAction: (member) => onAction('resend', member),
      },
      {
        id: 'remove',
        label: t('remove.action'),
        icon: <UserX aria-hidden />,
        variant: 'destructive',
        separatorBefore: true,
        hidden: (member) => !restrictions(member).canRemove,
        onAction: (member) => onAction('remove', member),
      },
    ];
  }, [actor, members, onAction, t]);
}
